import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, Info, X } from 'lucide-react';
import { apiCall } from '../../services/api.service';

/**
 * PlatformNoticeBar — the one strip at the top of the platform.
 *
 * Replaces the pattern of every concern growing its own banner. Before this,
 * TrialBanner decided on its own that it deserved the top of the screen; adding
 * arrears as a second one would have stacked two bars (three, with the
 * impersonation banner) and left no one able to say which mattered.
 *
 * All ranking happens server-side in BillingNoticeService, which returns exactly
 * one notice. This component paints it and nothing else — no thresholds, no
 * "if overdue then red" logic. Billing rules that live in a React component are
 * rules nobody can unit-test.
 *
 * Dismissal is per notice code with a server-provided TTL, so an informational
 * strip can be closed for a day while a critical one has no close button at all
 * (dismissible_hours = 0). A customer must not be able to click away the fact
 * that their service is about to stop.
 */

const STORAGE_PREFIX = 'grid.notice.dismissed.';

const SEVERITY_STYLES = {
  info: {
    bar: 'bg-blue-600 text-white',
    button: 'bg-surface text-blue-700 dark:text-blue-300 hover:bg-blue-50 hover:dark:bg-blue-900/30',
    Icon: Info,
  },
  warning: {
    bar: 'bg-amber-500 text-white',
    button: 'bg-surface text-amber-700 dark:text-amber-300 hover:bg-amber-50 hover:dark:bg-amber-900/30',
    Icon: CreditCard,
  },
  urgent: {
    bar: 'bg-orange-600 text-white',
    button: 'bg-surface text-orange-700 dark:text-orange-300 hover:bg-orange-50 hover:dark:bg-orange-900/30',
    Icon: AlertTriangle,
  },
  critical: {
    bar: 'bg-red-600 text-white',
    button: 'bg-surface text-red-700 dark:text-red-300 hover:bg-red-50 hover:dark:bg-red-900/30',
    Icon: AlertTriangle,
  },
};

const isDismissed = (code) => {
  if (!code) return false;
  try {
    const until = localStorage.getItem(`${STORAGE_PREFIX}${code}`);
    if (!until) return false;
    if (Date.now() < Number(until)) return true;
    localStorage.removeItem(`${STORAGE_PREFIX}${code}`);
    return false;
  } catch {
    return false;
  }
};

const rememberDismissal = (code, hours) => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${code}`, String(Date.now() + hours * 3600 * 1000));
  } catch {
    /* private mode — the notice simply comes back on reload */
  }
};

const PlatformNoticeBar = () => {
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await apiCall('billing/v1/me/billing-status', 'GET');
        if (cancelled || !data?.notice) return;
        if (isDismissed(data.notice)) return;
        setNotice(data);
      } catch {
        // Non-critical by design: a user without a billing account, or a
        // billing hiccup, must never surface an error at the top of every page.
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const dismiss = useCallback(() => {
    if (!notice) return;
    rememberDismissal(notice.notice, notice.dismissible_hours);
    setHidden(true);
  }, [notice]);

  if (!notice || hidden) return null;

  const style = SEVERITY_STYLES[notice.severity] || SEVERITY_STYLES.info;
  const { Icon } = style;
  const canDismiss = Number(notice.dismissible_hours) > 0;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full px-3 sm:px-4 py-2 ${style.bar}`}
    >
      <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />

        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold">{notice.headline}</span>
          {notice.detail && (
            <span className="block sm:inline sm:ml-2 text-xs opacity-90">{notice.detail}</span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {notice.cta_url && (
            <button
              type="button"
              onClick={() => navigate(notice.cta_url)}
              className={`min-h-[36px] rounded px-3 py-1.5 text-xs font-semibold transition-colors touch-manipulation ${style.button}`}
            >
              {notice.cta_label || 'Pay now'}
            </button>
          )}
          {canDismiss && (
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss notice"
              className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded hover:bg-black/10 transition-colors touch-manipulation"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlatformNoticeBar;
